/**
 * useCellSubscription — real-time store subscription for a single table cell.
 *
 * Subscribes to the entity's CRM object store entry using per-object
 * granularity (subscribeToObject). Only re-renders when this specific entity
 * changes, not on any unrelated store mutation.
 *
 * Update pipeline:
 *   Store change → 50ms debounce (collapse WS bursts) → requestAnimationFrame
 *   → state update + 300ms highlight animation
 *
 * Multiple property changes for the same entity that arrive within the
 * debounce window are batched into a single render.
 *
 * Subscription cleanup is handled automatically on unmount — this covers both
 * component teardown and rows scrolling out of view in a virtualized list.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSyncExternalStore } from 'react';

import { subscribeToStoredObject } from '@/lib/crm/store/crmObjectStore';
import { logger } from '@/lib/logger';

export interface CellSubscriptionResult {
  /** Latest property value from the store, undefined if entity not in store. */
  storeValue: unknown;
  /** True for ~300ms after an external update — drives the highlight animation. */
  isExternallyUpdated: boolean;
}

const DEBOUNCE_MS = 50;
// How long the external-update highlight is held before it starts fading.
// The fade itself is a slow CSS transition on the gridcell (see data-table)
// — the classic "yellow fade" pattern: appear fast, decay over ~2s total so
// the eye is drawn to the change without demanding action.
const HIGHLIGHT_MS = 900;

/**
 * Subscribe a table cell to its entity's property value in the CRM object store.
 *
 * @param entityId     - The entity's timeline ID (UUID)
 * @param propertyName - The property key to extract from store data
 */
export function useCellSubscription(
  entityId: string,
  propertyName: string
): CellSubscriptionResult {
  // Stable subscribe/getSnapshot pair — recreated only when entityId changes.
  // subscribeToStoredObject tracks subscription counts internally and cleans
  // up empty listener sets automatically.
  //
  // Deliberately the STORED-object variant, not subscribeToObject: the latter
  // only returns COMPLETE entries (full detail fetch), but a WebSocket event
  // for an object this tab never opened creates a PARTIAL entry — which is
  // exactly the common case in a list view. Filtering on completeness made
  // remote edits invisible to cells (no live value, no external-update flash)
  // unless the record happened to have been opened locally.
  const { subscribe, getSnapshot } = useMemo(
    () => subscribeToStoredObject(entityId),
    [entityId]
  );

  // Wrap getSnapshot to catch errors and log them for debugging.
  // This handles cases where the store is unavailable (WebSocket disconnected),
  // the property is missing, or there's corrupted state.
  const safeGetSnapshot = useMemo(
    () => () => {
      try {
        return getSnapshot();
      } catch (error) {
        logger.warn('useCellSubscription: Failed to get snapshot', {
          entityId,
          propertyName,
          error: error instanceof Error ? error.message : String(error),
          errorType:
            error instanceof Error ? error.constructor.name : typeof error,
        });
        // Return undefined to maintain fallback behavior
        return undefined;
      }
    },
    [getSnapshot, entityId, propertyName]
  );

  // Raw store entry — updates whenever this entity changes in the store.
  // Per-object subscription avoids re-renders from unrelated entity mutations.
  const storeObject = useSyncExternalStore(subscribe, safeGetSnapshot);

  // Extract the specific property value (PropertyValue wrapper → raw value).
  // Returns undefined if the entity is not in the store or the property is absent.
  const rawStoreValue = storeObject?.data.properties?.[propertyName]?.value;

  // Debounced/RAF-batched value — what we actually commit to render state.
  // Initialised from the store at mount so no spurious highlight on first render.
  const [debouncedValue, setDebouncedValue] = useState<unknown>(rawStoreValue);

  // True for HIGHLIGHT_MS after an external update to show the flash indicator.
  const [isExternallyUpdated, setIsExternallyUpdated] = useState(false);

  // Tracks the value we last committed to rendered state to skip no-op effects.
  const renderedValueRef = useRef<unknown>(rawStoreValue);

  // Track previous identity to detect cell recycling in virtualized lists.
  const prevIdentityRef = useRef({ entityId, propertyName });

  // Cleanup handles
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafHandleRef = useRef<number | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When the cell identity changes (virtualized row recycling), sync immediately
  // using the setState-during-render pattern so React discards the stale frame.
  // This avoids one render frame of showing the previous entity's value.
  if (
    prevIdentityRef.current.entityId !== entityId ||
    prevIdentityRef.current.propertyName !== propertyName
  ) {
    prevIdentityRef.current = { entityId, propertyName };

    // Cancel any pending debounce/RAF from the previous identity
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (rafHandleRef.current !== null) {
      cancelAnimationFrame(rafHandleRef.current);
      rafHandleRef.current = null;
    }
    if (highlightTimerRef.current !== null) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }

    // Immediately sync to the new identity's value (setState during render)
    renderedValueRef.current = rawStoreValue;
    setDebouncedValue(rawStoreValue);
    setIsExternallyUpdated(false);
  }

  useEffect(() => {
    // Always clear any pending debounce timer and RAF first — even if the new
    // value matches what's rendered. This prevents a stale timer or RAF from
    // committing an intermediate value when the store reverts quickly (A→B→A).
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (rafHandleRef.current !== null) {
      cancelAnimationFrame(rafHandleRef.current);
      rafHandleRef.current = null;
    }

    // Skip if nothing actually changed vs what is already rendered.
    // Avoids spurious debounce/RAF cycles when the store emits for unrelated
    // properties on the same entity.
    if (rawStoreValue === renderedValueRef.current) {
      return;
    }

    // Capture value now — the closure keeps the correct value even if
    // rawStoreValue changes again during the debounce window.
    const capturedValue = rawStoreValue;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;

      // Cancel any RAF queued by a prior debounce cycle.
      if (rafHandleRef.current !== null) {
        cancelAnimationFrame(rafHandleRef.current);
      }

      // Schedule the visual update on the next animation frame so all property
      // changes that arrived within the debounce window render together.
      rafHandleRef.current = requestAnimationFrame(() => {
        rafHandleRef.current = null;
        renderedValueRef.current = capturedValue;
        setDebouncedValue(capturedValue);

        // Trigger the brief highlight to signal an external update.
        setIsExternallyUpdated(true);
        if (highlightTimerRef.current !== null) {
          clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = setTimeout(() => {
          highlightTimerRef.current = null;
          setIsExternallyUpdated(false);
        }, HIGHLIGHT_MS);
      });
    }, DEBOUNCE_MS);
  }, [rawStoreValue]);

  // Cancel all pending timers/RAF on unmount.
  // Covers both explicit teardown and rows scrolling out of view in a
  // virtualized list — the component unmounts, clearing all pending updates.
  // Re-subscribe happens automatically when the row scrolls back into view
  // and the component remounts (useSyncExternalStore handles this).
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      if (rafHandleRef.current !== null) {
        cancelAnimationFrame(rafHandleRef.current);
      }
      if (highlightTimerRef.current !== null) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  return { storeValue: debouncedValue, isExternallyUpdated };
}
